import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { TableWrap } from '../../components/ui/Table'
import { remindersApi } from '../../api/reminders.api'
import { toast } from '../../store/toastStore'
import { ReminderFormModal } from './ReminderFormModal'
import type { ReminderTargetType } from '../../api/types'

const TARGET_LABELS: Record<ReminderTargetType, string> = {
  PERSONAL: 'Persönlich',
  TASK: 'Aufgabe',
  PROJECT: 'Projekt',
  ROADMAP: 'Roadmap',
}

const REPEAT_LABELS: Record<string, string> = {
  NONE: 'Einmalig',
  DAILY: 'Täglich',
  WEEKLY: 'Wöchentlich',
  MONTHLY: 'Monatlich',
}

export function RemindersPage() {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)

  const remindersQuery = useQuery({ queryKey: ['reminders'], queryFn: async () => (await remindersApi.list()).data })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remindersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      toast.success('Reminder gelöscht.')
    },
  })

  const reminders = remindersQuery.data ?? []

  return (
    <div>
      <PageHeader
        title="Reminder"
        subtitle="Eigene und rollenbezogene Erinnerungen."
        actions={
          <Button variant="primary" onClick={() => setShowForm(true)}>
            + Neuer Reminder
          </Button>
        }
      />

      {reminders.length === 0 ? (
        <EmptyState mascot title="Keine Reminder" description="Lege eine Erinnerung für eine Aufgabe, ein Projekt oder persönlich an." />
      ) : (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>Nachricht</th>
                <th>Bezug</th>
                <th>Fällig</th>
                <th>Wiederholung</th>
                <th>Für</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reminders.map((reminder) => (
                <tr key={reminder.id}>
                  <td>{reminder.message}</td>
                  <td>
                    <Badge>{TARGET_LABELS[reminder.targetType]}</Badge>
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{new Date(reminder.dueAt).toLocaleString('de-DE')}</td>
                  <td>{REPEAT_LABELS[reminder.repeat]}</td>
                  <td>{reminder.role ? `Rolle: ${reminder.role.name}` : 'Mich'}</td>
                  <td>
                    <Button size="sm" variant="danger" onClick={() => deleteMutation.mutate(reminder.id)}>
                      Löschen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {showForm ? <ReminderFormModal onClose={() => setShowForm(false)} /> : null}
    </div>
  )
}
